import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@shared/lib/cn";

/*
 * Кнопка системы Nocturne в оболочке shadcn.
 *
 * Имена вариантов оставлены шадсиэновскими намеренно: другие его компоненты
 * (диалог с действиями, всплывающее подтверждение) просят у кнопки
 * `variant="default"`. Переименуешь — и каждый следующий `shadcn add`
 * придётся чинить руками.
 *
 * Тона с подложкой — `default`, `secondary`, `destructive`, `danger` — на
 * материале панелей (класс `tone-btn`, правила в `ui.css`): ровная заливка,
 * кромка светлее сверху, набухание при нажатии. Это вариант «В» из проб без
 * стекла, выбранный 3 ноября вместо капли движка. `ghost` и `plain` — без
 * подложки.
 */
const buttonVariants = cva(
  [
    /*
     * `gap` и боковые поля заданы точными величинами, а не ступенями шкалы:
     * в системе они и стояли вне её — 6px и `--space-3 × 1.2`. Ступень дала
     * бы 5.6px и 9.8px, то есть тихую правку макета под удобство инструмента.
     * Снимать их со шкалы — решение дизайна, и принимать его тут нечем.
     */
    "inline-flex shrink-0 cursor-pointer items-center justify-center gap-[6px] whitespace-nowrap",
    "rounded-[var(--radius-pill)] border-0 bg-transparent",
    "font-semibold text-foreground",
    "transition-[background-color,color] duration-200 outline-none",
    "disabled:cursor-not-allowed disabled:opacity-45",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    /*
     * Кольца фокуса от shadcn здесь нет. В `base.css` объявлен глобальный
     * `:focus-visible` с обводкой акцентом — правило вне слоя, поэтому оно
     * бьёт утилиты Tailwind. Оставить `ring-[3px]` значило бы получить и
     * обводку, и тень поверх неё.
     */
  ].join(" "),
  {
    variants: {
      variant: {
        // Подложку тонов рисует `tone-btn` (`ui.css`); здесь от них только подпись.
        default: "text-[color:var(--ui-on-accent)]",
        secondary: "text-foreground",
        destructive: "text-[color:var(--ui-on-destructive)]",
        danger: "text-destructive",
        // Без подложки: действие рядом с текстом, а не отдельная кнопка.
        ghost: "bg-transparent text-link hover:bg-primary/10",
        /*
         * Голая кнопка: ни рамки, ни цвета, ни отклика на наведение — так
         * `.btn` и стоял в системе сам по себе, без добавки вида. Имя своё, не
         * шадсиэновское: его варианты заняты, а на новое ни один их компонент
         * не ссылается, так что `add` от него не сломается.
         */
        plain: "bg-transparent",
      },
      size: {
        /*
         * Высоты — набора v6: S 36, M 44, L 52. В окнах и настройках макета
         * стоит S с подписью 13.5 — она и у плотных рядов (`sm`), прежние 30
         * в наборе не встречаются.
         */
        default: "h-[36px] px-[17px] text-[14px]/[1.2]",
        sm: "h-[36px] px-[17px] text-[13.5px]/[1.2]",
        lg: "h-[44px] px-[20px] text-[15px]/[1.2]",
        icon: "size-9",
      },
    },
    compoundVariants: [
      /*
       * Боковые поля у `ghost` уже: без рамки кнопка и так отделена, а
       * широкие поля отодвинули бы её от текста, рядом с которым она стоит.
       */
      { variant: "ghost", size: "default", class: "px-2" },
      { variant: "ghost", size: "sm", class: "px-2" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type Variant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
type Size = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

/*
 * Тона с подложкой. Главная — акцент, `secondary` — светлая заливка для
 * второстепенного, `destructive` — красная, для необратимого, `danger` —
 * светлая с красной подписью, для сбросов и того, что можно отменить.
 */
const TONED = new Set<Variant>(["default", "secondary", "destructive", "danger"]);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const v: Variant = variant ?? "default";
  const s: Size = size ?? "default";
  const classes = cn(buttonVariants({ variant: v, size: s }), TONED.has(v) && s !== "icon" && "tone-btn", className);

  const Comp = asChild ? Slot.Root : "button";
  return <Comp data-slot="button" data-variant={v} data-size={s} className={classes} {...props} />;
}

export { Button, buttonVariants };
